import * as React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"

import { AppStore } from "../../store"
import { getWorkspaceId } from "../../util/GetWorkspaceId"

import ApplicationHeader from "./components/ApplicationHeader"
import { updateSupportMailAddress } from "../contextualhelp/actions"
import {
    initialize as initializePendingNotifications,
    setUnreadNotificationsCounter as setUnreadNotificationsCounterAction,
    increaseUnreadNotificationsCounter as increaseUnreadNotificationsCounterAction,
} from "./components/pendingnotifications/actions"
import { initializeSearch } from "../search/actions"
import {
    initialize as initializeSmartLocation,
    requestSiblings as requestRelativeSiblings,
} from "./components/smartlocation/actions"
import {
    initialize as initializeUserMenu,
    requestHomePageUrl as getHomePageUrlUpdate,
} from "./components/usermenu/actions"
import { updateSelectedTab } from "./components/navigation/actions"
import { updateDefaultNavigationTab as updateDefaultNavigationTabInUserMenu } from "./components/usermenu/actions"
import OldUIContext from "../../olduicontext"
import { isContextualHelpAvailableForUser } from "../contextualhelp/hardcodedDictionary"
import { IUserInfo } from "./components/usermenu"
import { IPendingNotifications } from "./components/pendingnotifications"
import { ISmartLocation } from "./components/smartlocation"
import { ISearch } from "../search"
import { IOldUIContext } from "../../global"

interface Props
    extends IPendingNotifications,
        Partial<ISearch>,
        ISmartLocation {
    isCniEnabled: boolean
    supportMailAddress: string
    userInfo: IUserInfo
}

interface OldUIInit {
    start: (element: HTMLElement | undefined | null, props: Props) => void
    pendingNotifications: PendingNotificationsInit
    smartLocation: SmartLocationInit
    userMenu: UserMenuInit
    navigationMenu: NavigationMenuInit
}

interface PendingNotificationsInit {
    setUnreadNotificationsCounter: (unreadNotificationsCounter: number) => void
    increaseUnreadNotificationsCounter: (count: number) => void
}
interface SmartLocationInit {
    refreshRelativeSiblings: () => void
}

interface UserMenuInit {
    updateHomePageUrl: () => void
}

interface NavigationMenuInit {
    updateDefaultNavigationTab: (selectedTab: number) => void
}

function init(store: AppStore, oldUIContext: IOldUIContext): OldUIInit {
    const start = (
        element: HTMLElement | undefined | null,
        props: Props,
    ): void => {
        if (!element) {
            throw new Error("Element should have value")
        }

        const {
            smartSearchInitData,
            isCniEnabled,
            userInfo,
            supportMailAddress,
        } = props

        store.dispatch(initializeSearch(getWorkspaceId(), smartSearchInitData))
        store.dispatch(initializePendingNotifications(props))
        store.dispatch(initializeSmartLocation(props))
        store.dispatch(initializeUserMenu(userInfo))

        const isContextualHelpEnabled = isContextualHelpAvailableForUser()

        if (isContextualHelpEnabled) {
            startContextualHelp(supportMailAddress)
        }

        const root = createRoot(element)
        root.render(
            <Provider store={store}>
                <OldUIContext.Provider value={oldUIContext}>
                    <OldUIContext.Consumer>
                        {(): JSX.Element => (
                            <ApplicationHeader
                                isSmartSearch={!!smartSearchInitData}
                                isCniEnabled={isCniEnabled}
                                isContextualHelpEnabled={
                                    isContextualHelpEnabled
                                }
                            />
                        )}
                    </OldUIContext.Consumer>
                </OldUIContext.Provider>
            </Provider>,
        )
    }

    const setUnreadNotificationsCounter = (
        unreadNotificationsCounter: number,
    ): void => {
        store.dispatch(
            setUnreadNotificationsCounterAction(unreadNotificationsCounter),
        )
    }

    const increaseUnreadNotificationsCounter = (count: number): void => {
        store.dispatch(increaseUnreadNotificationsCounterAction(count))
    }

    const refreshRelativeSiblings = (): void => {
        store.dispatch(requestRelativeSiblings())
    }

    const updateHomePageUrl = (): void => {
        store.dispatch(getHomePageUrlUpdate())
    }

    const updateDefaultNavigationTab = (selecedTab: number): void => {
        if (!!store.getState().navigation.initialized) {
            store.dispatch(updateSelectedTab(selecedTab))
        } else {
            // in case the user menu was not initialized yet
            // then it takes the starting value from the userMenu
            store.dispatch(updateDefaultNavigationTabInUserMenu(selecedTab))
        }
    }

    const startContextualHelp = async (
        supportMailAddress: string,
    ): Promise<void> => {
        store.dispatch(updateSupportMailAddress(supportMailAddress))
    }

    return {
        start,
        pendingNotifications: {
            setUnreadNotificationsCounter,
            increaseUnreadNotificationsCounter,
        },
        smartLocation: {
            refreshRelativeSiblings,
        },
        userMenu: {
            updateHomePageUrl,
        },
        navigationMenu: {
            updateDefaultNavigationTab,
        },
    }
}

export default init
